import {isCompletionChunk, type LogEntry} from '../../../types/session'
import {useLogs} from '../../../hooks/useLogs'
import {useCurrentUser} from '../../../contexts/CurrentUserContext'
import {CopilotMessageBubble, CopilotMessageContainer, CopilotMessageLoading} from './CopilotMessageBubble'
import {ResolvedPlanStatus} from '../../session-logs/ResolvedPlanStatus'
import {hasPrMetadataTags} from '../../../utils/pr-metadata-tags'
import {extractPlanContent, stripPlanTags} from '../../../utils/plan-tags'
import {getCustomAgentId, isCustomAgentSessionGroup} from '../../../utils/group-agent-logs'
import {useEffect} from 'react'
import {DismissedPlanDisplay} from '../../session-logs/DismissedPlanDisplay'
import type {PlanState} from '../../../hooks/use-patch-session'
import {SessionMetadataBar} from '../SessionFooter'
import {preprocessLogEntries} from '../../../utils/dedupe-logs-data'
import {TOOL_NAMES} from '../../../constants/tool-names'
import {findToolResponse} from '../../session-logs/prompt-log-utils'

/** CCA-specific actions for end-of-session plan approval */
export const CCA_PLAN_ACTION_IMPLEMENT = 'implement'
export const CCA_PLAN_ACTION_EXIT = 'exit_only'
export const CCA_PLAN_ACTIONS = [CCA_PLAN_ACTION_IMPLEMENT, CCA_PLAN_ACTION_EXIT]

interface CopilotFinalStopMessageProps {
  logsPollingInterval: number
  /** Called when the user approves the plan — parent should kick off a new session */
  onApprove?: (action: string, planSummary: string) => void
  /** Plan state: undefined = pending, approved = resolved chip, dismissed = read-only bubble */
  planState?: PlanState
  /**
   * Called with the plan content string when an actionable plan is detected,
   * or null when no actionable plan is present. The parent uses this to render
   * the plan approval UI in the bottom slot (replacing the chat input).
   */
  onPlanDetected?: (planContent: string | null) => void
}

export function CopilotFinalStopMessage({
  logsPollingInterval,
  onApprove,
  planState,
  onPlanDetected,
}: CopilotFinalStopMessageProps) {
  const {currentUserIsSessionOwner} = useCurrentUser()
  const {
    data: logsData,
    isLoading: logsIsLoading,
    error: logsError,
  } = useLogs({logsPollingInterval, currentUserIsSessionOwner})

  const stopMessages = extractStopMessages(logsData)
  const combinedContent = stopMessages.map(m => m.content).join('\n\n')
  const codeReviewResult = stopMessages.length === 0 ? extractLatestCodeReviewResult(logsData) : ''
  const {planContent, remainingContent} = extractPlanContent(combinedContent)

  const hasPlanContent = !!planContent
  const isActionablePlan = hasPlanContent && !!onApprove && !planState

  useEffect(() => {
    onPlanDetected?.(isActionablePlan ? planContent : null)
  }, [isActionablePlan, planContent, onPlanDetected])

  if (logsIsLoading) {
    return <CopilotMessageLoading />
  }

  if (logsError || (stopMessages.length === 0 && !codeReviewResult)) {
    return <SessionMetadataBar placement="outside-session" />
  }

  const copyText = stripPlanTags(combinedContent || codeReviewResult)
  const metadataBar = <SessionMetadataBar placement="outside-session" copyText={copyText} />

  // The code_review tool already renders its result in the session log.
  if (stopMessages.length === 0) {
    return metadataBar
  }

  // No plan tags — render everything as regular markdown
  if (!planContent) {
    return (
      <>
        <CopilotMessageContainer>
          <CopilotMessageBubble markdown={copyText} />
        </CopilotMessageContainer>
        {metadataBar}
      </>
    )
  }

  // Dismissed plan — render read-only with border
  if (planState === 'dismissed') {
    return (
      <>
        <DismissedPlanDisplay planContent={planContent} />
        {metadataBar}
      </>
    )
  }

  if (planState === 'approved') {
    return (
      <>
        <ResolvedPlanStatus />
        {metadataBar}
      </>
    )
  }

  // Only render the remaining (non-plan) content here.
  // The PlanApprovalDisplay is rendered by the parent in the bottom slot.
  return (
    <>
      {remainingContent && (
        <CopilotMessageContainer>
          <CopilotMessageBubble markdown={remainingContent} />
        </CopilotMessageContainer>
      )}
      {metadataBar}
    </>
  )
}

/**
 * Extracts trailing assistant stop messages from log entries, excluding PR metadata.
 * Only messages at the end of the log (after all tool calls) are extracted,
 * matching the filtering behavior in preprocessLogEntries.
 */
function extractStopMessages(logsData: LogEntry[] = []): Array<{id: string; content: string}> {
  const messages: Array<{id: string; content: string}> = []

  // Walk backward from the end to find trailing stop entries
  for (let i = logsData.length - 1; i >= 0; i--) {
    const logEntry = logsData[i]
    if (!logEntry) break

    // Skip non-CompletionChunk entries (e.g. lifecycle events like session.idle)
    if (!isCompletionChunk(logEntry)) continue

    // Skip subagent entries
    if (getCustomAgentId(logEntry)) break

    const choice = logEntry.choices[0]
    const hasToolCalls = choice?.delta.tool_calls && choice.delta.tool_calls.length > 0

    // Skip empty stop chunks (no content, no tool calls)
    if (choice?.finish_reason === 'stop' && !choice.delta.content && !hasToolCalls) {
      continue
    }

    if (
      choice?.finish_reason === 'stop' &&
      choice.delta.role === 'assistant' &&
      choice.delta.content &&
      !hasToolCalls
    ) {
      // PR metadata can follow the user-facing summary. Ignore it without ending
      // the backward scan so the preceding final response remains copyable.
      if (hasPrMetadataTags(choice.delta.content)) {
        continue
      }

      const content = choice.delta.content.trim()
      if (content) {
        messages.unshift({id: logEntry.id, content})
      }
      continue
    }

    // Hit a non-stop entry — stop
    break
  }

  return messages
}

function extractLatestCodeReviewResult(logsData: LogEntry[] = []): string {
  const {entries} = preprocessLogEntries([...logsData])

  for (let i = entries.length - 1; i >= 0; i--) {
    const entry = entries[i]
    if (!entry) continue
    if (isCustomAgentSessionGroup(entry)) continue
    if (!isCompletionChunk(entry)) return ''
    if (getCustomAgentId(entry)) continue

    for (let j = entry.choices.length - 1; j >= 0; j--) {
      const choice = entry.choices[j]
      if (!choice) continue

      const toolCalls = choice.delta.tool_calls ?? []
      const content = choice.delta.content?.trim()

      if (choice.finish_reason === 'stop' && !content && toolCalls.length === 0) {
        continue
      }

      if (choice.finish_reason === 'stop' && content && toolCalls.length === 0 && hasPrMetadataTags(content)) {
        continue
      }

      const codeReviewToolCalls = toolCalls.filter(
        toolCall =>
          toolCall.function?.name === TOOL_NAMES.CODE_REVIEW || toolCall.custom?.name === TOOL_NAMES.CODE_REVIEW,
      )

      if (codeReviewToolCalls.length > 0 && codeReviewToolCalls.length === toolCalls.length) {
        for (let k = codeReviewToolCalls.length - 1; k >= 0; k--) {
          const toolCallId = codeReviewToolCalls[k]?.id
          if (!toolCallId) continue

          const response = findToolResponse(toolCallId, logsData)?.trim()
          if (response) {
            return response
          }
        }
      }

      return ''
    }
  }

  return ''
}
